// Template282: Bali - Plan de viaje (Page2.jsx)
// Cabecera verde #2F6B4F, días en grid 2 columnas, Información útil, 3 extras en fila.

import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";

const Template282 = ({
	backgroundColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	onNavigateToPage,
	pages = [],
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const pageData = page?.data || {};
	const diasRaw = Array.isArray(pageData.dias) ? pageData.dias : [];
	const extrasRaw = Array.isArray(pageData.extras) ? pageData.extras : [];

	const diasTemplateMapping =
		Array.isArray(pageData.diasTemplateMapping) &&
		pageData.diasTemplateMapping.length > 0
			? pageData.diasTemplateMapping
			: [];
	const diasOrderMapping =
		Array.isArray(pageData.diasOrderMapping) &&
		pageData.diasOrderMapping.length > 0
			? pageData.diasOrderMapping
			: [];
	const extrasTemplateMapping =
		Array.isArray(pageData.extrasTemplateMapping) &&
		pageData.extrasTemplateMapping.length > 0
			? pageData.extrasTemplateMapping
			: [];

	const findPageByTemplateId = (templateId, index = 0) => {
		const matches = pages.filter(
			(p) => p.templateId === templateId || p.data?.templateId === templateId
		);
		return matches[index] || matches[0] || null;
	};
	const findPageByOrder = (orderVal) => {
		if (orderVal == null || !pages.length) return null;
		return (
			pages.find(
				(p) =>
					(p.order !== undefined && p.order === orderVal) ||
					(p.data?.order !== undefined && p.data.order === orderVal)
			) || null
		);
	};
	const getPageId = (p) => p?.id ?? p?.data?.id;
	const getPageIndex = (p) => (p && pages.length ? pages.indexOf(p) : -1);

	const dias = diasRaw.map((dia, index) => {
		let targetPage = null;
		if (diasOrderMapping[index] != null) {
			targetPage = findPageByOrder(diasOrderMapping[index]);
		}
		if (!targetPage && diasTemplateMapping[index] != null) {
			const templateId = diasTemplateMapping[index];
			const occurrence =
				templateId == null
					? 0
					: diasTemplateMapping.slice(0, index).filter((t) => t === templateId)
							.length;
			targetPage = findPageByTemplateId(templateId, occurrence);
		}
		const pageId = dia.pageId || (targetPage ? getPageId(targetPage) : null);
		const pageIndex = targetPage ? getPageIndex(targetPage) : -1;
		return {
			...dia,
			pageId: pageId || undefined,
			pageIndex: pageIndex >= 0 ? pageIndex : undefined,
		};
	});

	const extras = extrasRaw.map((extra, index) => {
		const templateId = extrasTemplateMapping[index] ?? null;
		const occurrence =
			templateId == null
				? 0
				: extrasTemplateMapping.slice(0, index).filter((t) => t === templateId)
						.length;
		const targetPage = templateId
			? findPageByTemplateId(templateId, occurrence)
			: null;
		const pageId = extra.pageId || (targetPage ? getPageId(targetPage) : null);
		const pageIndex = targetPage ? getPageIndex(targetPage) : -1;
		return {
			...extra,
			pageId: pageId || undefined,
			pageIndex: pageIndex >= 0 ? pageIndex : undefined,
		};
	});

	const actualHeaderColor = headerBackgroundColor || "#2F6B4F";
	const headerText = page?.texts?.[0]?.content ?? "Plan de viaje";

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found?.imageUrl) return found.imageUrl;
		}
		return undefined;
	};
	const logoImage = getCanvasImageUrl("logo") || "/images/logo.png";

	const handleNavigate = (pageIdOrIndex) => {
		if (
			onNavigateToPage &&
			pageIdOrIndex !== undefined &&
			pageIdOrIndex !== null
		)
			onNavigateToPage(pageIdOrIndex);
	};

	const renderTituloMovil = (dia) => {
		const movil = dia.tituloMovil;
		if (typeof movil === "string") return movil.replace(/\\n/g, "\n");
		return dia.titulo;
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "100%",
				width: "100%",
				backgroundColor: "#f7f7f7",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				position: "relative",
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
			}}
		>
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontSize: "14px",
					fontWeight: 600,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1,
				}}
			>
				{headerText}
				<Box
					component="img"
					src={logoImage}
					alt="logo"
					sx={{
						width: isSmallScreen ? 66 : 80,
						position: "absolute",
						right: 2,
						top: 18,
						zIndex: 999999,
					}}
				/>
			</Box>

			<Box data-block-id="text-1" sx={{ padding: isSmallScreen ? "10px 12px" : "20px 16px" }}>
				<Box
					data-block-id="text-2"
					sx={{
						display: "grid",
						gridTemplateColumns: "1fr 1fr",
						gap: isSmallScreen ? 1.25 : 1.5,
						overflow: "visible",
					}}
				>
					{dias.map((dia, index) => (
						<Box
							key={index}
							onClick={() => handleNavigate(dia.pageId ?? dia.pageIndex)}
							sx={{
								borderRadius: "12px",
								padding: 0,
								display: "flex",
								alignItems: "center",
								gap: isSmallScreen ? "5px" : "10px",
								position: "relative",
								minHeight: 30,
								cursor:
									(dia.pageId ?? dia.pageIndex) != null ? "pointer" : "default",
								transition: "transform 0.3s ease",
								"&:hover":
									(dia.pageId ?? dia.pageIndex) != null
										? {
												transform: "translateY(-5px)",
												"& .bali-dia-box": {
													boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
												},
										  }
										: {},
							}}
						>
							{dia.image && (
								<Box
									component="img"
									src={dia.image}
									alt=""
									sx={{
										width: isSmallScreen ? 35 : 45,
										zIndex: 100,
										position: "absolute",
										left: 8,
									}}
								/>
							)}
							<Box
								className="bali-dia-box"
								sx={{
									backgroundColor: "#fff",
									borderRadius: "12px",
									padding: isSmallScreen ? "10px" : "14px 16px",
									display: "flex",
									alignItems: "center",
									gap: "10px",
									position: "relative",
									minHeight: 30,
									width: "100%",
									textAlign: "left",
									flex: 1,
									transition: "box-shadow 0.3s ease",
									paddingLeft: isSmallScreen ? "48px" : "60px",
								}}
							>
								<Box
									sx={{
										fontSize: isSmallScreen ? "10px" : "13px",
										fontWeight: 600,
										color: "#333",
										marginLeft: 1,
										whiteSpace: "pre-line",
									}}
								>
									{isSmallScreen
										? `Día ${index + 1}:\n${renderTituloMovil(dia)}`
										: `Día ${index + 1}: ${dia.titulo}`}
								</Box>
							</Box>
						</Box>
					))}
				</Box>

				<Box
					component="h3"
					sx={{
						fontSize: 12,
						fontWeight: 700,
						margin: 0,
						textTransform: "uppercase",
						marginTop: 2,
						marginBottom: 1,
						color: "#333",
					}}
				>
					Información útil
				</Box>

				<Box
					data-block-id="text-3"
					sx={{
						display: "grid",
						gridTemplateColumns: `repeat(${extras.length}, 1fr)`,
						gap: 1,
						paddingBottom: "2px",
					}}
				>
					{extras.map((extra, index) => (
						<Box
							key={index}
							onClick={() => handleNavigate(extra.pageId ?? extra.pageIndex)}
							sx={{
								backgroundColor: "#fff",
								borderRadius: "12px",
								padding: isSmallScreen ? "0px 5px 2.5px 5px" : "10px 8px",
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								textAlign: "center",
								justifyContent: "center",
								cursor:
									(extra.pageId ?? extra.pageIndex) != null
										? "pointer"
										: "default",
								transition: "transform 0.3s ease, box-shadow 0.3s ease",
								"&:hover":
									(extra.pageId ?? extra.pageIndex) != null
										? {
												transform: "translateY(-5px)",
												boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
										  }
										: {},
							}}
						>
							{extra.image && (
								<Box
									component="img"
									src={extra.image}
									alt=""
									sx={{
										width: 50,
										height: 57,
										objectFit: "contain",
										marginBottom: 0.75,
									}}
								/>
							)}
							<Box
								sx={{
									fontSize: isSmallScreen ? "10px" : "12px",
									fontWeight: 600,
									color: "#333",
									lineHeight: 1.3,
									marginTop: -1.875,
								}}
							>
								{extra.titulo}
							</Box>
						</Box>
					))}
				</Box>
			</Box>
		</Box>
	);
};

Template282.thumbnail = "/images/template-1.png";
export default Template282;
